import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DateService } from '../date/date.service';
import { DateParserService } from '../daily-log/daily-log-time/date-parser-service';
import { Safety, SafetyReport } from '../../models/safety/safety.models';
import { PtLuxonDate } from '../../models/projects/date.models';
import { Observable } from 'rxjs/Observable';
import { AppConfig } from '../../app/app.config';
import { cloneDeep } from 'lodash';

@Injectable()
export class SafetyService {
  config = AppConfig.settings;

  constructor(
    private http: HttpClient,
    private dateService: DateService,
    private dateParserService: DateParserService
  ) {
  }

  updateSafetyTotals(originalSafetyEditor: Safety, updatedSafetyEditor: Safety): Safety {
    updatedSafetyEditor = this.updateWeeklyDataLocally(originalSafetyEditor, updatedSafetyEditor);
    updatedSafetyEditor = this.updateReadingTotalsLocally(originalSafetyEditor, updatedSafetyEditor);

    return updatedSafetyEditor;
  }

  // weekly data isn't stored in the DB, it's calculated on load
  // we can update the UI without needing to reload by applying the difference between the original and updated value when the user makes a change
  updateWeeklyDataLocally(originalSafetyEditor: Safety, updatedSafetyEditor: Safety): Safety {
    if (originalSafetyEditor.drills !== updatedSafetyEditor.drills) {
      const drillsDifference = updatedSafetyEditor.drills - originalSafetyEditor.drills;
      updatedSafetyEditor.weeklyDrills = originalSafetyEditor.weeklyDrills + drillsDifference;
    }

    if (originalSafetyEditor.safetyMeetings !== updatedSafetyEditor.safetyMeetings) {
      const safetyMeetingsDifference = updatedSafetyEditor.safetyMeetings - originalSafetyEditor.safetyMeetings;
      updatedSafetyEditor.weeklySafetyMeetings = originalSafetyEditor.weeklySafetyMeetings + safetyMeetingsDifference;
    }

    if (originalSafetyEditor.toolboxTalks !== updatedSafetyEditor.toolboxTalks) {
      const toolboxTalksDifference = updatedSafetyEditor.toolboxTalks - originalSafetyEditor.toolboxTalks;
      updatedSafetyEditor.weeklyToolboxTalks = originalSafetyEditor.weeklyToolboxTalks + toolboxTalksDifference;
    }

    return updatedSafetyEditor;
  }

  updateReadingTotalsLocally(originalSafetyEditor: Safety, updatedSafetyEditor: Safety): Safety {
    updatedSafetyEditor.readings.forEach(updatedReading => {
      const originalReading = originalSafetyEditor.readings.find(e => e.category.id === updatedReading.category.id);

      if (originalReading && originalReading.value !== updatedReading.value) {
        const difference = updatedReading.value - originalReading.value;
        updatedReading.ptdValue = originalReading.ptdValue + difference;
        updatedReading.ytdValue = originalReading.ytdValue + difference;
      }
    });

    return updatedSafetyEditor;
  }

  async getSafetyEditorForDay(dailyLogDate: PtLuxonDate): Promise<Safety> {
    // toMillis returns the utc time
    const dateLong = dailyLogDate.dateTime.toMillis();

    const url = `${this.config.apiUrl}api/safety/geteditor/${dateLong}`;
    const result = await this.http.get<Safety>(url).toPromise();
    return await this.setSafetyEditorLocalDates(result);
  }

  async setSafetyEditorLocalDates(safetyEditor: Safety): Promise<Safety> {
    if (safetyEditor.date) {
      safetyEditor.date = await this.dateService.setPtLuxonDateFromApi(safetyEditor.date);
    }

    return safetyEditor;
  }

  // note that resulting editor.date has not been deserialised here, as all we need is the id
  async saveEditor(editor: Safety): Promise<Safety> {
    const editorToSave = cloneDeep(editor);
    editorToSave.date.dateTime = this.dateParserService.removeOffsetFromLuxonDate(editorToSave.date.dateTime);
    const url = `${this.config.apiUrl}api/safety/save`;
    return this.http.post<Safety>(url, editorToSave).toPromise();
  }


  validate(editor: Safety): Observable<void> {
    const editorToValidate = this.dateParserService.removeOffsetFromLuxonDates(cloneDeep(editor));
    const url = `${this.config.apiUrl}api/safety/validate`;
    return this.http.post<void>(url, editorToValidate);
  }

  validateReport(editor: SafetyReport): Observable<void> {
    const url = `${this.config.apiUrl}api/safety/report/validate`;
    return this.http.post<void>(url, editor);
  }
}



// WEBPACK FOOTER //
// ./src/services/safety/safety.service.ts